import { useState } from "react";
import { Link } from "react-router-dom";
import { Send } from "lucide-react";
import { LOGO_URL, NAV_LINKS } from "../lib/constants";
import { useSubmit } from "../lib/api";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const { loading, submit } = useSubmit("/newsletter");

  const onJoin = async (e) => {
    e.preventDefault();
    const res = await submit({ email, name: name || undefined });
    if (res?.success) {
      setEmail("");
      setName("");
    }
  };

  return (
    <footer className="grain relative bg-garden-dark text-white" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="" className="h-14 w-14 rounded-full object-cover ring-2 ring-pollen/60" />
              <div className="leading-tight">
                <span className="block font-serif text-xl font-semibold italic text-pollen">Bee Courageous</span>
                <span className="block text-[10px] font-bold uppercase tracking-[0.3em] text-white/70">@ Garden Grub</span>
              </div>
            </div>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/70">
              A faith-based nonprofit healing program where women move from trauma and instability toward restoration,
              self-sufficiency, and leadership — rooted in faith, nourished by food, and lifted by community.
            </p>
            <p className="mt-5 font-serif text-lg italic text-pollen-soft/80">
              "She is clothed with strength and dignity; she can laugh at the days to come." — Proverbs 31:25
            </p>
          </div>

          <nav className="lg:col-span-3" aria-label="Footer navigation">
            <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-pollen">Explore</h3>
            <ul className="mt-4 grid grid-cols-1 gap-2">
              {NAV_LINKS.map((l) => (
                <li key={l.path}>
                  <Link to={l.path} className="text-sm text-white/70 transition-colors hover:text-pollen" data-testid={`footer-${l.path === "/" ? "home" : l.path.slice(1)}`}>
                    {l.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="lg:col-span-5">
            <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-pollen">Join the Hive</h3>
            <p className="mt-4 text-sm leading-relaxed text-white/70">
              Encouragement, garden updates, event invitations, and stories of restoration — straight to your inbox.
            </p>
            <form onSubmit={onJoin} className="mt-5 flex flex-col gap-3 sm:flex-row" data-testid="footer-newsletter-form">
              <label htmlFor="footer-name" className="sr-only">First name (optional)</label>
              <input
                id="footer-name"
                className="input-field !border-white/20 !bg-white/10 !text-white placeholder:!text-white/50 sm:max-w-[140px]"
                placeholder="First name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="footer-newsletter-name"
              />
              <label htmlFor="footer-email" className="sr-only">Email address</label>
              <input
                id="footer-email"
                type="email"
                required
                className="input-field !border-white/20 !bg-white/10 !text-white placeholder:!text-white/50"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="footer-newsletter-email"
              />
              <button type="submit" disabled={loading} className="btn-pollen shrink-0 disabled:opacity-60" data-testid="footer-newsletter-submit">
                {loading ? "Joining…" : <>Sign Up <Send className="h-4 w-4" aria-hidden="true" /></>}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-xs text-white/50 sm:flex-row">
          <p>© {new Date().getFullYear()} Bee Courageous @ Garden Grub. A faith-based nonprofit healing program.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="transition-colors hover:text-pollen" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/accessibility" className="transition-colors hover:text-pollen" data-testid="footer-accessibility">Accessibility</Link>
            <Link to="/admin" className="transition-colors hover:text-pollen" data-testid="footer-admin">Team</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
